import { useEffect, useState } from 'react';
import { Grid, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Autocomplete, CircularProgress } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { globalPostService, globalPutService } from '../../../utils/globalApiServices';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { checkApiStatus, getGlobalCookie } from '../../../utils';
import { backendDateFormat, fieldDateFormat } from '../../../constants';
import { changeSummaryInvoiceStatusApi, getInvoiceConstant } from '../apiService';
import { jetStreamInstance, PageLoader } from '../../../shared_elements';
import { trackActivity } from '../../../utils/mixpanel';
import NumericLabel from 'react-pretty-numbers';
import { regexConstants } from '../../../constants/regEx';

const ChangeSummaryInvStatus = ({ onToggleModal, invoiceStatus, getInvoice=()=>{}, getResponseBack, invoiceType, getLinkedInvoices=()=>{} }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setLoading] = useState(false);
    const [invoice, setInvoice] = useState({ ...invoiceStatus?.data, payment_date: null, sent_date: moment().format(backendDateFormat), partial_payment_amount: null, partial_payment_date: null } || {})
    const [error, setError] = useState({});
    const [invoiceConstants, setInvoiceConstants] = useState([]);
   
    useEffect(() => {
        getInvoiceConstants()
    }, [])

    const onFieldChange = (keyParam, value) => {
        setInvoice({ ...invoice, [keyParam]: value })
    }

    const onRestErrorKey = (keyParam) => {
        setError({ ...error, [keyParam]: '' })
    }

    const getInvoiceConstants = () => {
        setLoading(true);
        globalPostService(getInvoiceConstant(), { 'constant_types': ["invoice_status"] })
            .then(response => {
                if (checkApiStatus(response)) {
                    setLoading(false);
                    setInvoiceConstants(response?.data?.data);
                }
            })
    }

    const linkedStatus = invoiceStatus?.data?.status
    const filteredInvoiceStatus = invoiceConstants?.filter(status =>
        linkedStatus?.value === 1 ? (jetStreamInstance ? [2, 6].includes(status?.value) :[2].includes(status?.value) ):
        linkedStatus?.value === 2 ? (jetStreamInstance ? [3, 5, 6].includes(status?.value) : [3].includes(status?.value)) :
        linkedStatus?.value === 5 ? (jetStreamInstance ? [3, 5, 6].includes(status?.value) : [3].includes(status?.value)) :
        true
    )?.sort((a, b) => (a?.value === 4 ? 1 : 0) - (b?.value === 4 ? 1 : 0));

    const onChangeInvoiceStatus = () => {

        let validationInputs = {};
        const statusValue = invoice?.status?.value;

        if (statusValue === 3) {
            const pd = invoice?.payment_date;
            let paymentDateError = "";

            if (!pd) {
                paymentDateError = "Payment Date is required.";
            } else {
                const m = moment(pd, backendDateFormat, true);

                if (!m.isValid()) {
                    paymentDateError = "Please enter a valid date.";
                } else {
                    if (m.isAfter(moment())) {
                        paymentDateError = "Payment Date cannot be in the future.";
                    }
                    else if (invoiceStatus?.data?.sent_date && m.isBefore(moment(invoiceStatus?.data?.sent_date))) {
                        paymentDateError = `Payment Date must be on or after the Sent Date (${moment(invoiceStatus?.data?.sent_date).format(fieldDateFormat)}).`;
                    }
                }
            }
            validationInputs = {
                ...validationInputs,
                payment_date: paymentDateError
            };
        }

        if (statusValue === 2) {
            const sd = invoice?.sent_date;
            let sentDateError = "";

            if (!sd) {
                sentDateError = "Sent Date is required.";
            } else {
                const m = moment(sd, backendDateFormat, true);
                if (!m.isValid()) {
                    sentDateError = "Please enter a valid date.";
                } else if (m.isAfter(moment())) {
                    sentDateError = "Sent Date cannot be in the future.";
                } else if (invoice?.date && m.isBefore(moment(invoice?.date))) {
                    sentDateError = `Sent Date must be on or after the Invoice Date (${moment(invoice.date).format(fieldDateFormat)}).`;
                }
            }
            validationInputs = {
                ...validationInputs,
                sent_date: sentDateError
            };
        }

        if (statusValue === 4) {
            validationInputs = { reason: invoice?.reason ? "" : "Please enter Reason", };
        }

        if (statusValue === 5) {
            const amt = invoice?.partial_payment_amount;
            const totalAmt = invoiceStatus?.data?.total_amount;
            let amountError = "";
            if (!amt && amt !== 0) {
                amountError = "Partially Paid Amount is required.";
            } else if (isNaN(Number(amt)) || Number(amt) <= 0) {
                amountError = "Please enter a valid amount.";
            } else if (Number(amt) >= Number(totalAmt)) {
                amountError = "Partially Paid Amount must be less than the total amount.";
            }

            const ppd = invoice?.partial_payment_date;
            let partialDateError = "";
            if (!ppd) {
                partialDateError = "Partially Paid Date is required.";
            } else {
                const m = moment(ppd, backendDateFormat, true);
                if (!m.isValid()) {
                    partialDateError = "Please enter a valid date.";
                } else if (m.isAfter(moment())) {
                    partialDateError = "Partially Paid Date cannot be in the future.";
                } else if (invoiceStatus?.data?.date && m.isBefore(moment(invoiceStatus?.data?.date))) {
                    partialDateError = `Partially Paid Date must be on or after the Invoice Date (${moment(invoiceStatus?.data?.date).format(fieldDateFormat)}).`;
                }
            }
            validationInputs = { ...validationInputs, partial_payment_amount: amountError, partial_payment_date: partialDateError };
        }

        // Prepare payload
        let payload = {
            slug: invoice?.slug || null,
            status: invoice?.status || null,
        };

        if (statusValue === 4) {
            payload.reason = invoice?.reason || null;
        }

        if (invoice?.status?.value === 2) {
            payload.sent_date = invoice?.sent_date || null;
        }

        if (statusValue === 3) {
            payload.payment_date = invoice?.payment_date || null;
        }

        if (statusValue === 5) {
            payload.partial_payment_amount = invoice?.partial_payment_amount || null;
            payload.partial_payment_date = invoice?.partial_payment_date || null;
        }

        const isValid =
            (statusValue !== 2 && statusValue !== 3 && statusValue !== 4 && statusValue !== 5) ||
            Object.keys(validationInputs).every((k) => validationInputs[k] === "");

        if (isValid) {
            setLoading(true);
            globalPutService(changeSummaryInvoiceStatusApi(invoice?.slug), payload).then((response) => {
                if (checkApiStatus(response)) {
                    onToggleModal();
                    setLoading(false);
                    getInvoice();
                    getResponseBack();
                    getLinkedInvoices();
                    enqueueSnackbar(response?.data?.message, {
                        variant: "success",
                        anchorOrigin: { horizontal: "right", vertical: "top" },
                    });
                    trackActivity('Item Edited', { page_title: `Changes Summary Invoice Status`, success_msg: response?.data?.message, payload, response: response?.data })
                } else {
                    setLoading(false);
                    enqueueSnackbar(response?.data?.message, {
                        variant: "error",
                        anchorOrigin: { horizontal: "right", vertical: "top" },
                    });
                    trackActivity('Item Edited', { page_title: `Changes Summary Invoice Status`, error_msg: response?.data?.message, payload, response: response?.data })
                }
            });
        } else {
            setError(validationInputs);
            trackActivity(`Item Edited`, { page_title: `Changes Summary Invoice Status`, error_msg: validationInputs })
        }
    };

    const handleStatusChange = (newValue) => {
        setInvoice({ ...invoice, status: newValue, payment_date: null, partial_payment_amount: null, partial_payment_date: null });
        setError({});
    }

    const isDataChanged = () => {
        const originalStatus = invoiceStatus?.data?.status?.value;
        const currentStatus = invoice?.status?.value;
        if (currentStatus !== originalStatus) return true;
        if (currentStatus === 2 && invoice?.sent_date !== invoiceStatus?.data?.sent_date) return true;
        if (currentStatus === 5 && (invoice?.partial_payment_amount || invoice?.partial_payment_date)) return true;
        return false;
    };

    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: `Change Summary Invoice Status`, page_type: page_type, })
    }

    return (
        <>
            <Dialog
                aria-labelledby="customized-dialog-title"
                className='add-lease-dialog'
                maxWidth='md'
                open={invoiceStatus.modal}
            >
                <DialogTitle sx={{ m: 0, p: 2 }} className='dialog-header' id="customized-dialog-title">
                    Change Invoice Status
                </DialogTitle>
                <DialogContent dividers style={{ minWidth: "370px", maxWidth: "370px", minHeight: "100px", padding: "16px" }}>
                    <Grid container spacing={2}>
                        <Grid size={{ md: 12, xs: 12 }}>
                            <Autocomplete
                                disablePortal
                                options={filteredInvoiceStatus}
                                getOptionLabel={(option) => option.label || ""}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                value={invoice?.status}
                                onFocus={() => onRestErrorKey("status")}
                                disableClearable
                                onChange={(e, newValue) => handleStatusChange(newValue)}
                                onInputChange={(event, newInputValue) => {
                                    if (newInputValue.trim() !== "") {
                                        setError((prev) => ({ ...prev, currency: "" }));
                                    }
                                }}
                                slotProps={{
                                    paper: {
                                        sx: {
                                            maxHeight: [4, 3].includes(invoice?.status?.value) ? 130 : 90,
                                            overflowY: 'auto',
                                        },
                                    },
                                }}
                                renderInput={(params) => (<TextField required {...params} label="Status" margin='normal' size='small' helperText={error.status} slotProps={{ inputLabel: { shrink: true } }} />)}
                            />
                        </Grid>
                        {invoice?.status?.value === 2 ? <Grid size={{ md: 12, xs: 12 }}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label="Sent Date"
                                    value={invoice?.sent_date ? moment(invoice.sent_date) : null}
                                    onChange={(data) => {
                                        const isValid = moment(data)?.isValid();
                                        const formattedDate = isValid ? moment(data).format(backendDateFormat) : null;
                                        onFieldChange("sent_date", formattedDate);
                                        if (isValid) {
                                            if (moment(data).isAfter(moment(), 'day')) {
                                                setError((prev) => ({ ...prev, sent_date: "Sent Date cannot be in the future." }));
                                            } else {
                                                onRestErrorKey("sent_date");
                                            }
                                        }
                                    }}
                                    disableFuture
                                    minDate={moment(invoice?.date)}
                                    slotProps={{
                                        textField: {
                                            required: "true",
                                            InputLabelProps: { shrink: true },
                                            size: "small",
                                            margin: "normal",
                                            error: error.sent_date || false,
                                            helperText: error.sent_date || '',
                                            onFocus: () => {
                                                if (error.sent_date) {
                                                    onRestErrorKey("sent_date");
                                                }
                                            }
                                        },
                                        field: {
                                            clearable: true,
                                            onClear: () => {
                                                onFieldChange("sent_date", null);
                                                onRestErrorKey("sent_date");
                                            }
                                        },
                                    }}
                                    sx={{ width: "100%" }}
                                    format={fieldDateFormat}
                                />
                            </LocalizationProvider>
                        </Grid> : null}
                        {invoice?.status?.value === 3 ?
                            <Grid size={{ md: 12, xs: 12 }}>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        label="Payment Date"
                                        value={invoice?.payment_date ? moment(invoice.payment_date) : null}
                                        onChange={(data) => {
                                            const isValid = moment(data)?.isValid();
                                            const formattedDate = isValid ? moment(data).format(backendDateFormat) : null;
                                            onFieldChange("payment_date", formattedDate);
                                            if (isValid) {
                                                onRestErrorKey("payment_date");
                                            }
                                        }}
                                        disableFuture
                                        minDate={moment(invoiceStatus?.data?.sent_date)}
                                        slotProps={{
                                            textField: {
                                                required: "true",
                                                InputLabelProps: { shrink: true },
                                                size: "small",
                                                margin: "normal",
                                                error: error.payment_date || false,
                                                helperText: error.payment_date || '',
                                                onFocus: () => {
                                                    if (error.payment_date) {
                                                        onRestErrorKey("payment_date");
                                                    }
                                                }
                                            },
                                            field: {
                                                clearable: true,
                                                onClear: () => {
                                                    onFieldChange("date", null);
                                                    onRestErrorKey("date");
                                                }
                                            },
                                        }}
                                        sx={{ width: "100%" }}
                                        format={fieldDateFormat}
                                    />
                                </LocalizationProvider>
                            </Grid> : null}
                        {invoice?.status?.value === 5 ? (
                            <>
                                <Grid size={{ md: 12, xs: 12 }}>
                                    <TextField
                                        label="Partially Paid Amount"
                                        variant="outlined"
                                        size='small'
                                        value={invoice?.partial_payment_amount ?? ''}
                                        onChange={(e) => {
                                            const cleanValue = e.target.value.replace(/[^0-9.]/g, '');
                                            if (regexConstants.decimalnodotstart.test(cleanValue)) {
                                                onFieldChange('partial_payment_amount', cleanValue);
                                                onRestErrorKey('partial_payment_amount');
                                            }
                                        }}
                                        required
                                        margin='normal'
                                        fullWidth
                                        slotProps={{ inputLabel: { shrink: true } }}
                                        error={!!error?.partial_payment_amount}
                                        helperText={error?.partial_payment_amount || <span>Must be less than total amount (<NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: invoiceStatus?.data?.currency, precision: 2, commafy: true, justification: 'L' }}>{invoiceStatus?.data?.total_amount || 0}</NumericLabel>)</span>}
                                        onFocus={() => onRestErrorKey('partial_payment_amount')}
                                    />
                                </Grid>
                                <Grid size={{ md: 12, xs: 12 }}>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        <DatePicker
                                            label="Partially Paid Date"
                                            value={invoice?.partial_payment_date ? moment(invoice.partial_payment_date) : null}
                                            onChange={(data) => {
                                                const isValid = moment(data)?.isValid();
                                                const formattedDate = isValid ? moment(data).format(backendDateFormat) : null;
                                                onFieldChange('partial_payment_date', formattedDate);
                                                if (isValid) onRestErrorKey('partial_payment_date');
                                            }}
                                            disableFuture
                                            minDate={invoiceStatus?.data?.date ? moment(invoiceStatus?.data?.date) : undefined}
                                            slotProps={{
                                                textField: {
                                                    required: true,
                                                    InputLabelProps: { shrink: true },
                                                    size: 'small',
                                                    margin: 'normal',
                                                    error: !!error.partial_payment_date,
                                                    helperText: error.partial_payment_date || '',
                                                    onFocus: () => { if (error.partial_payment_date) onRestErrorKey('partial_payment_date'); }
                                                },
                                                field: {
                                                    clearable: true,
                                                    onClear: () => { onFieldChange('partial_payment_date', null); onRestErrorKey('partial_payment_date'); }
                                                },
                                            }}
                                            sx={{ width: '100%' }}
                                            format={fieldDateFormat}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                            </>
                        ) : null}
                        {invoice?.status?.value === 4 ?
                            <Grid item size={{ md: 12, xs: 12 }}>
                                <TextField
                                    id="status_change_reason"
                                    label="Reason"
                                    variant="outlined"
                                    size='small'
                                    value={invoice?.reason || ''}
                                    onChange={(e) => onFieldChange('reason', e.target.value.trimStart())}
                                    required
                                    margin='normal'
                                    fullWidth
                                    multiline
                                    rows={3}
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    sx={{ '& .MuiInputBase-input': { resize: 'vertical', overflow: 'auto' } }}
                                    error={error?.reason}
                                    helperText={error?.reason || ''}
                                    onFocus={() => onRestErrorKey("reason")}
                                />
                            </Grid> : null}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ paddingRight: "16px" }}>
                    <Button className='cancel-btn' variant='outlined' size='small' disabled={isLoading} onClick={() => { onToggleModal(); handleItemClicked('Cancel Button') }} > Cancel </Button>
                    <Button variant='contained' className='btn-primary' disabled={isLoading || !isDataChanged()} size='small' onClick={() => { onChangeInvoiceStatus(); handleItemClicked('Save Button') }}>
                        {isLoading ? <CircularProgress color='inherit' size={25} /> : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
            {isLoading ? <PageLoader /> : null}
        </>
    )
}
export default ChangeSummaryInvStatus;
